/* Brixi AI — frontend API client.
 *
 * `chatStream` consumes the SSE response from POST /api/ai/chat and invokes
 * onEvent callbacks per event type. Everything else is plain JSON.
 *
 * IMPORTANT: regular axios requests go through the project's request
 * interceptor which injects the JWT. The streaming endpoint uses fetch()
 * (EventSource cannot send custom headers), so we re-use `getStoredAccessToken`
 * directly to attach the same token here.
 */
import API from "./api";
import { getStoredAccessToken } from "../utils/authSession";

const SSE_URL = "/ai/chat";

/** Fetch greeting, suggestions, available tools. */
export async function fetchAIWelcome() {
  const { data } = await API.get("/ai/welcome");
  return data;
}

export async function fetchAIHistory(limit = 50) {
  const { data } = await API.get("/ai/history", { params: { limit } });
  return data;
}

export async function fetchAIConversation(id) {
  const { data } = await API.get(`/ai/history/${id}`);
  return data;
}

export async function deleteAIConversation(id) {
  await API.delete(`/ai/history/${id}`);
}

export async function renameAIConversation(id, title) {
  const { data } = await API.patch(`/ai/history/${id}/rename`, { title });
  return data;
}

export async function pinAIConversation(id, pinned) {
  const { data } = await API.patch(`/ai/history/${id}/pin`, { pinned });
  return data;
}

/** Build the same Authorization header the axios interceptor would attach. */
function getAuthHeaders() {
  const token = getStoredAccessToken();
  const headers = { ...(API.defaults.headers.common || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Stream a chat turn. Uses fetch + ReadableStream so the auth header travels with the request
 * (EventSource cannot send custom headers).
 *
 * @param {object} body — { message, conversation_id?, context? }
 * @param {object} handlers — onEvent(type, data), onError(err), onClose(), abortSignal?
 */
export async function chatStream(body, handlers = {}) {
  const { onEvent, onError, onClose, abortSignal } = handlers;
  const baseURL = API.defaults.baseURL || "";
  const url = `${baseURL}${SSE_URL}`;

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
      signal: abortSignal,
    });
  } catch (err) {
    onError?.(err);
    onClose?.();
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    let friendly = `Chat request failed (${response.status}).`;
    try {
      const j = JSON.parse(text);
      friendly = j?.detail || j?.message || friendly;
    } catch {
      if (text && text.length < 240) friendly = text;
    }
    if (response.status === 401) friendly = "Your session expired. Please sign in again.";
    else if (response.status === 403) friendly = "You don't have permission to use Brixigo AI here.";
    else if (response.status === 429) friendly = "Too many requests — please wait a moment.";
    else if (response.status >= 500) friendly = "Brixigo AI is temporarily unavailable. Please retry shortly.";
    onError?.(new Error(friendly));
    onClose?.();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE records are separated by a blank line.
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const record = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const { event, data } = parseSSERecord(record);
        if (event === ":keepalive") continue;
        if (data === undefined) continue;
        try {
          const parsed = JSON.parse(data);
          // server payload shape: { type, data }
          const type = parsed.type || event;
          const payload = parsed.data ?? parsed;
          onEvent?.(type, payload);
        } catch {
          onEvent?.(event || "text", { delta: data });
        }
      }
    }
  } catch (err) {
    if (err?.name !== "AbortError") onError?.(err);
  } finally {
    onClose?.();
  }
}

function parseSSERecord(record) {
  const lines = record.split("\n");
  let event = null;
  let data = "";
  for (const line of lines) {
    if (line.startsWith(":")) {
      event = ":keepalive";
      continue;
    }
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  return { event, data: data || undefined };
}
